import React, { useRef } from 'react'
import { ResponsiveContainer, Legend, BarChart, Area, Bar, AreaChart, CartesianGrid, XAxis, YAxis, LineChart, Line, Tooltip } from 'recharts'
import classNames from 'classnames'
import Card from 'components/Detail/Card'
import ChartTooltip from '../../../../containers/CustomDashboardv2/Dashboard/details/charts/ChartTooltip'
import IncidentChartMenu from './components/IncidentChartMenu'
import { chartGradientDefs } from '../../../../containers/CustomDashboardv2/Dashboard/details/charts/utils'
import { LegendItems } from './components/shapes'
import { LabelError } from './components/labels'

const IncidentCartesianChart = ({ color, isLoading, title, chartData = [], chartDef = {}, chartId, height, legendPosition = 'right', chartHeight, isAnimationActive = true, onExplain, showAI, errorMessage, enableLegendTooltip = false }) => {
  const { xDataKey = 'name', disableXKey, metrics = [], chartType, stackKeys, xTicks, xDomain, legendIcon, gradientOpacity, isStacked: isStackedFromDef } = chartDef
  const wrapperProps = {
    data: chartData,
    barGap: 2,
    margin: { top: 5, right: 30, left: 0, bottom: 5 }
  }

  const isStacked = Boolean(isStackedFromDef || (Array.isArray(stackKeys) && stackKeys.length > 0))

  const renderChartWrapper = (children) => {
    switch (chartType) {
      case 'line':
        return <LineChart {...wrapperProps}>{children}</LineChart>
      case 'area':
      case 'mixed':
        return <AreaChart {...wrapperProps}>{children}</AreaChart>
      case 'bar':
      default:
        return (
          <BarChart {...wrapperProps} stackOffset={isStacked ? 'none' : undefined}>
            {children}
          </BarChart>
        )
    }
  }

  const getStackId = (key) => {
    if (!stackKeys) return undefined
    const idx = stackKeys.findIndex((group) => group.includes(key))
    const stackId = idx === -1 ? 'stack' : `stack${idx}`
    return stackId
  }

  const renderChartComponent = (props) => {
    if (chartType === 'mixed') {
      if (props.variant === 'area') {
        return <Area {...props} type='monotone' stroke={props.stroke} isAnimationActive={isAnimationActive} />
      }
      return <Line {...props} type='monotone' stroke={props.stroke} isAnimationActive={isAnimationActive} />
    }

    switch (chartType) {
      case 'line':
        return <Line {...props} type='monotone' stroke={props.stroke} isAnimationActive={isAnimationActive} />
      case 'area':
        return <Area {...props} type='monotone' stroke={props.stroke} isAnimationActive={isAnimationActive} />
      case 'bar':
      default:
        return <Bar {...props} stroke={null} isAnimationActive={isAnimationActive} />
    }
  }

  const chartRef = useRef(null)

  return (
    <Card
      noGrid
      menu={onExplain && showAI && 'dot'}
      color={color}
      isLoading={isLoading}
      className={classNames('viso-chart-card', isLoading && 'viso-chart-card--loading')}
      content={
        <div className='viso-chart-card__content viso-chart-card__content--cartesian' style={{ height: height }} id={chartId}>
          <div className='viso-chart-card__title viso-chart-card__title--cartesian'>{title}</div>

          <div className='viso-chart-card__container viso-chart-card__container--cartesian'>
            <div ref={chartRef} className={classNames('viso-chart-card__data viso-chart-card__data--cartesian', `viso-chart-card__data--legend-${legendPosition}`)}>
              {!isLoading && chartData.length === 0 ?
                <LabelError message={errorMessage} />
                :
                <ResponsiveContainer width='100%' height={chartHeight}>
                  {renderChartWrapper(
                    <>
                      <CartesianGrid strokeDasharray="3 3" />

                      <XAxis
                        dataKey={disableXKey ? undefined : xDataKey}
                        dy={0}
                        dx={-0}
                        label={{ value: '', angle: 0, position: 'bottom' }}
                        ticks={xTicks}
                        domain={xDomain}
                        tick={{ fontSize: 12, angle: 0 }}
                      />

                      <YAxis tick={{ fontSize: 12, angle: 0 }} />

                      <Tooltip
                        isAnimationActive={false}
                        wrapperStyle={{ visibility: 'hidden' }}
                        cursor={{ fill: 'transparent' }}
                        content={(props) => (
                          <ChartTooltip
                            chartDef={chartDef}
                            rechartsProps={props}
                            chartContentRef={chartRef}
                            staticColors={metrics.map(metric => ({ name: metric.dataKey, color: metric.fill }))}
                          />
                        )}
                      />

                      {metrics.map((item, index) => {
                        const chartFill = item.fill
                        const dataKeySafe = item.dataKey.replaceAll(' ', '_')
                        const fillId = `${chartId}_${dataKeySafe}`

                        const isLineChart = chartType === 'line'
                        const isAreaChart = chartType === 'area'
                        const isMixedChart = chartType === 'mixed'
                        const isBarChart = !isLineChart && !isAreaChart && !isMixedChart

                        const isAreaSeries =
                          isAreaChart ||
                          (isMixedChart && item.variant === 'area')

                        const shouldUseGradientFill =
                          isBarChart ||
                          isAreaSeries ||
                          (isLineChart && !isMixedChart)

                        const componentProps = {
                          dataKey: item.dataKey,
                          stackId: stackKeys && getStackId(item.dataKey),
                          dot: typeof item.dot !== 'undefined' ? item.dot : false,
                          connectNulls: typeof item.connectNulls !== 'undefined' ? item.connectNulls : false,
                          fill: shouldUseGradientFill ? `url(#${fillId}Fill)` : 'none',
                          stroke: chartFill,
                          strokeWidth: item.strokeWidth,
                          variant: isAreaSeries ? 'area' : 'line'
                        }

                        return (
                          <React.Fragment key={index}>
                            {shouldUseGradientFill && chartGradientDefs(fillId, chartFill, gradientOpacity)}
                            {renderChartComponent(componentProps)}
                          </React.Fragment>
                        )
                      })}

                      <Legend
                        content={
                          <LegendItems
                            iconType={legendIcon || 'square'}
                            legendPosition={legendPosition}
                            metrics={chartDef.metrics}
                            truncateLegendLabels={chartDef.truncateLegendLabels}
                            enableLegendTooltip={enableLegendTooltip}
                          />
                        }
                        layout={legendPosition === 'right' ? 'vertical' : 'horizontal'}
                        align={legendPosition === 'right' ? 'right' : 'center'}
                        verticalAlign={legendPosition === 'right' ? 'middle' : 'bottom'}
                        wrapperStyle={{
                          top: legendPosition === 'right' && '50%',
                          transform: legendPosition === 'right' && 'translateY(-50%)',
                          height: legendPosition === 'right' && 'auto',
                          bottom: legendPosition === 'right' && 'auto'
                        }}
                      />
                    </>
                  )}
                </ResponsiveContainer>
              }
            </div>
          </div>
        </div>
      }
    >
      {onExplain && showAI && <IncidentChartMenu onExplain={() => onExplain({ data: chartData, chartType: chartType })} />}
    </Card>
  )
}

export default IncidentCartesianChart